import {
  AppBskyActorProfile,
  AppBskyFeedLike,
  AppBskyFeedPost,
  AppBskyFeedRepost,
  AppBskyGraphBlock,
  AppBskyGraphFollow,
  AtUri,
} from '@atproto/api'
import { db, getCachedPost, getCachedProfileByDid, listCached } from '../cache/db'
import type { Collection } from '../api/client'
import { blobUrl } from './handle'

type Embed = {
  $type?: string
  images?: Array<{ image?: { ref?: { $link?: string } | string }; alt?: string }>
  external?: { uri?: string; title?: string; description?: string }
  media?: Embed
  video?: unknown
}

type ExportRow = {
  uri: string
  rkey: string
  did: string
  authorHandle?: string
  authorDisplay?: string
  createdAt?: string
  text?: string
  embed?: Embed
  verb?: 'liked' | 'reposted' | 'followed' | 'blocked'
  verbedAt?: string
}

function blobLink(b: unknown): string | null {
  if (!b) return null
  if (typeof b === 'string') return b
  const o = b as { ref?: unknown }
  if (typeof o.ref === 'string') return o.ref
  if (o.ref && typeof o.ref === 'object') {
    const r = o.ref as { $link?: string }
    if (typeof r.$link === 'string') return r.$link
  }
  return null
}

async function hydrateRows(handle: string, collection: Collection): Promise<ExportRow[]> {
  const cached = await listCached(handle.toLowerCase(), collection)
  const out: ExportRow[] = []
  for (const r of cached) {
    const v = r.value
    if (AppBskyFeedPost.isRecord(v)) {
      out.push({
        uri: r.uri,
        rkey: r.rkey,
        did: new AtUri(r.uri).hostname,
        createdAt: v.createdAt,
        text: v.text,
        embed: v.embed as Embed | undefined,
      })
    } else if (AppBskyFeedLike.isRecord(v) || AppBskyFeedRepost.isRecord(v)) {
      const verb = AppBskyFeedLike.isRecord(v) ? 'liked' : 'reposted'
      const subject = v.subject
      const cachedPost = await getCachedPost(subject.uri)
      const at = new AtUri(subject.uri)
      if (cachedPost && AppBskyFeedPost.isRecord(cachedPost.value)) {
        const post = cachedPost.value
        out.push({
          uri: subject.uri,
          rkey: at.rkey,
          did: at.hostname,
          createdAt: post.createdAt,
          text: post.text,
          embed: post.embed as Embed | undefined,
          verb,
          verbedAt: v.createdAt,
        })
      } else {
        out.push({
          uri: subject.uri,
          rkey: at.rkey,
          did: at.hostname,
          verb,
          verbedAt: v.createdAt,
        })
      }
    } else if (AppBskyGraphFollow.isRecord(v) || AppBskyGraphBlock.isRecord(v)) {
      const verb = AppBskyGraphFollow.isRecord(v) ? 'followed' : 'blocked'
      const profile = await getCachedProfileByDid(v.subject)
      const profileVal =
        profile && profile.profile && AppBskyActorProfile.isRecord(profile.profile)
          ? profile.profile
          : null
      out.push({
        uri: r.uri,
        rkey: r.rkey,
        did: v.subject,
        authorHandle: profile?.handle,
        authorDisplay: profileVal?.displayName,
        verb,
        verbedAt: v.createdAt,
      })
    }
  }
  return out
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

export async function exportJson(handle: string, collection: Collection) {
  const rows = await hydrateRows(handle, collection)
  const json = JSON.stringify(rows, null, 2)
  downloadBlob(new Blob([json], { type: 'application/json' }), `${handle}-${collection}.json`)
}

export async function exportText(handle: string, collection: Collection) {
  const rows = await hydrateRows(handle, collection)
  const lines: string[] = [`# ${handle} - ${collection}`, `# ${rows.length} records`, '']
  for (const r of rows) {
    lines.push('---')
    if (r.verb && r.verbedAt) lines.push(`${r.verb} at ${r.verbedAt}`)
    if (r.createdAt) lines.push(`posted at ${r.createdAt}`)
    if (r.authorHandle)
      lines.push(`@${r.authorHandle}${r.authorDisplay ? ` (${r.authorDisplay})` : ''}`)
    else if (r.did) lines.push(r.did)
    if (r.text) lines.push('', r.text)
    if (r.embed?.external?.uri) lines.push('', `link: ${r.embed.external.uri}`)
    if (r.embed?.$type?.includes('video')) lines.push('[video - not exported]')
    lines.push('')
  }
  downloadBlob(
    new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' }),
    `${handle}-${collection}.txt`,
  )
}

const ASCII = (s: string) => s.replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '')

async function fetchImage(
  did: string,
  cid: string,
): Promise<{ buf: Uint8Array; type: 'jpg' | 'png' } | null> {
  try {
    const url = blobUrl(did, cid, 'feed_fullsize')
    const res = await fetch(url)
    if (!res.ok) return null
    const ct = res.headers.get('content-type') ?? ''
    const buf = new Uint8Array(await res.arrayBuffer())
    if (ct.includes('jpeg') || ct.includes('jpg')) return { buf, type: 'jpg' }
    if (ct.includes('png')) return { buf, type: 'png' }
    if (buf[0] === 0xff && buf[1] === 0xd8) return { buf, type: 'jpg' }
    if (buf[0] === 0x89 && buf[1] === 0x50) return { buf, type: 'png' }
    return null
  } catch {
    return null
  }
}

export async function exportPdf(
  handle: string,
  collection: Collection,
  onProgress?: (done: number, total: number) => void,
) {
  const { PDFDocument, PageSizes, StandardFonts, rgb } = await import('pdf-lib')
  const rows = await hydrateRows(handle, collection)
  onProgress?.(0, rows.length)

  const doc = await PDFDocument.create()
  const helv = await doc.embedFont(StandardFonts.Helvetica)
  const helvBold = await doc.embedFont(StandardFonts.HelveticaBold)
  const helvOblique = await doc.embedFont(StandardFonts.HelveticaOblique)

  const ACCENT = rgb(0.22, 0.74, 0.97)
  const TEXT = rgb(0.886, 0.91, 0.949)
  const MUTED = rgb(0.58, 0.62, 0.71)
  const BG = rgb(0.06, 0.09, 0.16)
  const DIVIDER = rgb(0.12, 0.16, 0.25)

  const margin = 40
  const [pw, ph] = PageSizes.A4
  const innerWidth = pw - margin * 2

  let page = doc.addPage(PageSizes.A4)
  page.drawRectangle({ x: 0, y: 0, width: pw, height: ph, color: BG })
  let y = ph - margin

  const wrap = (text: string, font: typeof helv, size: number, maxWidth: number) => {
    const out: string[] = []
    for (const para of text.split('\n')) {
      const tokens = para.split(/(\s+)/)
      let line = ''
      for (const t of tokens) {
        const test = line + t
        if (font.widthOfTextAtSize(test, size) > maxWidth && line.trim()) {
          out.push(line.trimEnd())
          line = t.trimStart()
        } else {
          line = test
        }
      }
      out.push(line.trimEnd())
    }
    return out
  }

  const ensure = (space: number) => {
    if (y - space < margin) {
      page = doc.addPage(PageSizes.A4)
      page.drawRectangle({ x: 0, y: 0, width: pw, height: ph, color: BG })
      y = ph - margin
    }
  }
  const line = (text: string, font: typeof helv, size: number, color = TEXT) => {
    ensure(size + 4)
    page.drawText(ASCII(text), { x: margin, y: y - size, size, font, color })
    y -= size + 4
  }
  const multi = (text: string, font: typeof helv, size: number, color = TEXT) => {
    for (const ln of wrap(ASCII(text), font, size, innerWidth)) {
      ensure(size + 3)
      page.drawText(ln, { x: margin, y: y - size, size, font, color })
      y -= size + 3
    }
  }

  line(handle, helvBold, 22, ACCENT)
  line(`${collection} - ${rows.length} records`, helv, 11, MUTED)
  y -= 8

  let done = 0
  for (const row of rows) {
    ensure(80)
    line(row.authorDisplay || row.authorHandle || row.did, helvBold, 12)
    if (row.authorHandle) line(`@${row.authorHandle}`, helv, 9, MUTED)
    if (row.verb && row.verbedAt) {
      line(
        `${row.verb} at ${new Date(row.verbedAt).toLocaleString()}`,
        helvOblique,
        9,
        ACCENT,
      )
    }
    if (row.createdAt) {
      line(`posted at ${new Date(row.createdAt).toLocaleString()}`, helv, 9, MUTED)
    }
    if (row.text) {
      y -= 2
      multi(row.text, helv, 11)
    }

    const images: { cid: string; alt?: string }[] = []
    const collect = (e?: Embed) => {
      if (!e) return
      if (Array.isArray(e.images)) {
        for (const im of e.images) {
          const cid = blobLink(im.image)
          if (cid) images.push({ cid, alt: im.alt })
        }
      }
      if (e.media) collect(e.media)
    }
    collect(row.embed)

    for (const img of images.slice(0, 4)) {
      const data = await fetchImage(row.did, img.cid)
      if (!data) continue
      try {
        const embedded =
          data.type === 'jpg' ? await doc.embedJpg(data.buf) : await doc.embedPng(data.buf)
        const maxH = 220
        const scale = Math.min(innerWidth / embedded.width, maxH / embedded.height, 1)
        const w = embedded.width * scale
        const h = embedded.height * scale
        ensure(h + 12)
        page.drawImage(embedded, { x: margin, y: y - h, width: w, height: h })
        y -= h + 4
        if (img.alt) multi(img.alt, helvOblique, 8, MUTED)
      } catch {
        /* skip */
      }
    }

    if (row.embed?.external?.uri) {
      const ext = row.embed.external
      line(`link: ${ext.uri}`, helv, 9, ACCENT)
      if (ext.title) line(ext.title, helvBold, 10)
      if (ext.description) multi(ext.description, helv, 9, MUTED)
    }
    if (row.embed?.$type?.includes('video')) {
      line('[video - not exported]', helvOblique, 9, MUTED)
    }
    ensure(20)
    y -= 4
    page.drawLine({
      start: { x: margin, y },
      end: { x: pw - margin, y },
      thickness: 0.5,
      color: DIVIDER,
    })
    y -= 12
    done += 1
    if (done % 5 === 0) onProgress?.(done, rows.length)
  }

  const bytes = await doc.save()
  onProgress?.(rows.length, rows.length)
  const ab = new ArrayBuffer(bytes.byteLength)
  new Uint8Array(ab).set(bytes)
  downloadBlob(new Blob([ab], { type: 'application/pdf' }), `${handle}-${collection}.pdf`)
}

export async function totalCacheSize() {
  const [r, p, pr] = await Promise.all([db.records.count(), db.posts.count(), db.profiles.count()])
  return { records: r, posts: p, profiles: pr }
}
